import PropTypes from "prop-types";
import { useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { format, isValid, parse } from "date-fns";
import { CalendarIcon, XIcon } from "lucide-react";
import get from "lodash/get";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { dateNavBounds } from "components/NewAdvancedSearch/filters/dateNavBounds";

import { REQUIRED_MARK } from "./fieldClasses";

const DATE_FORMAT = "yyyy-MM-dd";
const DISPLAY_FORMAT = "PP";

// Parsed in local time and re-formatted to reject rolled-over values such as
// "2026-02-30", so a picked day never shifts across a timezone.
const toDate = text => {
  if (!text) return undefined;
  const date = parse(text, DATE_FORMAT, new Date());
  return isValid(date) && format(date, DATE_FORMAT) === text ? date : undefined;
};

/**
 * Date-only picker: a button that opens a calendar in a popover. Dates cannot
 * be typed or pasted. The value is a `YYYY-MM-DD` string, or null when empty;
 * an optional field shows a clear button once it has a value. The calendar
 * renders in a portal, so cards and scrolling tables do not clip it.
 */
const FormDatePicker = ({
  name,
  label,
  required,
  placeholder = "Pick a date",
  disabled,
  id,
  "aria-label": ariaLabel,
  onValueChange,
}) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name);
  const triggerId = id ?? name;
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => {
        const selected = toDate(field.value);
        const isClearable = !required && !disabled && Boolean(field.value);

        const commit = text => {
          field.onChange(text);
          field.onBlur();
          onValueChange?.(text);
        };

        const handlePick = date => {
          commit(date ? format(date, DATE_FORMAT) : null);
          setIsOpen(false);
        };

        return (
          <Field data-invalid={error ? true : undefined}>
            {label && (
              <FieldLabel
                htmlFor={triggerId}
                className={required ? REQUIRED_MARK : undefined}
              >
                {label}
              </FieldLabel>
            )}
            <div className="tw:relative">
              <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger
                  disabled={disabled}
                  render={
                    <Button
                      id={triggerId}
                      name={field.name}
                      variant="outline"
                      aria-label={ariaLabel}
                      aria-required={required || undefined}
                      aria-invalid={error ? true : undefined}
                      className={cn(
                        "tw:w-full tw:justify-start tw:font-normal",
                        isClearable && "tw:pr-8"
                      )}
                    />
                  }
                >
                  <CalendarIcon className="tw:text-muted-foreground" />
                  {selected ? (
                    <span className="tw:truncate">
                      {format(selected, DISPLAY_FORMAT)}
                    </span>
                  ) : (
                    <span className="tw:truncate tw:text-muted-foreground">
                      {field.value || placeholder}
                    </span>
                  )}
                </PopoverTrigger>
                <PopoverContent
                  className="tw:w-auto tw:overflow-hidden tw:p-0"
                  align="start"
                >
                  <Calendar
                    mode="single"
                    selected={selected}
                    defaultMonth={selected}
                    onSelect={handlePick}
                    captionLayout="dropdown"
                    {...dateNavBounds()}
                  />
                </PopoverContent>
              </Popover>
              {isClearable && (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className="tw:absolute tw:top-1/2 tw:right-1 tw:-translate-y-1/2"
                  aria-label={`Clear ${label ?? ariaLabel ?? "date"}`}
                  onClick={() => commit(null)}
                >
                  <XIcon />
                </Button>
              )}
            </div>
            <FieldError>{error?.message}</FieldError>
          </Field>
        );
      }}
    />
  );
};

FormDatePicker.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  required: PropTypes.bool,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  id: PropTypes.string,
  "aria-label": PropTypes.string,
  onValueChange: PropTypes.func,
};

export default FormDatePicker;
